/**
 * Table row for a specialty.
 */
import * as React from 'react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { SPECIALTY_STATUS_CONFIG } from './specialty-configs';
import { getSpecialtyStatusKey } from './specialty-table-utils';
import type { Specialty } from '@/types/specialty.types';

/** Props for {@link SpecialtyRow}. */
interface SpecialtyRowProps {
  specialty: Specialty;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (specialty: Specialty) => void;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean };
}

/** Table row for a specialty. */
export const SpecialtyRow: React.FC<SpecialtyRowProps> = ({
  specialty,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canToggle = pageActions?.canDeleteRow ?? true;

  const statusKey = getSpecialtyStatusKey(specialty.isActive);
  const statusConfig = SPECIALTY_STATUS_CONFIG[statusKey];
  const StatusIcon = statusConfig.icon;

  return (
    <TableRow>
      <td>
        <span className="font-mono text-sm text-muted-foreground">{specialty.code}</span>
      </td>

      <td>
        <span className="font-medium text-foreground">{specialty.label}</span>
      </td>

      <td>
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(specialty.id)} />
          {canEdit && <Button preset="row-edit" onClick={() => onEdit?.(specialty.id)} />}
          {canToggle && <Button preset="row-delete" onClick={() => onDelete?.(specialty)} />}
        </div>
      </td>
    </TableRow>
  );
};
